import {
  ASN,
  Batch,
  Case,
  Container,
  Damaged,
  FreightShipment,
  Generic,
  Pallet,
  ParcelShipment,
  Place,
  Product,
  UnitOfMeasure,
} from '../../api/models'
import { Action, ApplyScan, Mode } from './interface'

import { Handler, HandlerInput } from './base'

export type MoveHandlerCallback = (data: any) => void

const getKey = (product: Generic, sku?: Generic, productCase?: Case, batch?: Batch) => {
  return `${product.object.guid}-${sku?.object?.guid}-${productCase?.guid}-${batch?.guid}`
}

interface MoveItemArgs {
  product: Generic
  sku?: Generic
  skus: Generic[]
  productCase?: Case
  batch?: Batch
  batches?: Batch[]
}

export class MoveItem {
  product: Generic
  sku?: Generic
  skus: Generic[]
  productCase?: Case
  batch?: Batch
  batches?: Batch[]
  count: number

  constructor(item: MoveItemArgs) {
    this.product = item.product
    this.sku = item.sku
    this.skus = item.skus
    this.productCase = item.productCase
    this.batch = item.batch
    this.batches = item.batches
    this.count = 0
  }

  get key() {
    return getKey(this.product, this.sku, this.productCase, this.batch)
  }
}

class MoveItemList {
  items: MoveItem[]

  constructor() {
    this.items = []
  }

  move = (item: MoveItem, mode: Mode, count: number = 1) => {
    const key = item.key
    const index = this.items.map((g) => g.key).indexOf(key)
    let oldItem: MoveItem
    if (index > -1) {
      oldItem = this.items[index]
      item.count = oldItem.count
      this.items.splice(index, 1)
    }
    if (mode === Mode.Scan) {
      item.count += count
    } else {
      item.count -= count
    }
    if (item.count !== 0) {
      this.items.splice(0, 0, item)
    }
  }
}

interface MoveHandlerInput extends HandlerInput {
  source: Generic
}

export class MoveHandler extends Handler {
  source: Generic
  inventory: MoveItemList
  mode: Mode
  lastCaseMap: { [product: string]: Case | undefined }
  lastBatchMap: { [product: string]: Batch | undefined }
  toMeasure?: MoveItemArgs

  constructor(props: MoveHandlerInput) {
    super(props)
    this.source = props.source
    this.mode = Mode.Scan
    this.inventory = new MoveItemList()
    this.lastCaseMap = {}
    this.lastBatchMap = {}
  }

  apply = ({ scanned, context, local }: ApplyScan): [Action, any | undefined] => {
    switch (scanned.object.type) {
      case Case.type:
      case Product.type: {
        const product = context?.product || scanned
        const sku = context?.sku
        const skus = context?.skus || (sku ? [sku] : [])
        const batches = context?.batches
        const batch = batches && batches.length === 1 ? batches[0] : undefined
        const productCase =
          scanned.objectType === Case.type ? scanned.object : this.lastCaseMap[product.object.guid]
        if (context?.sku?.object.unitOfMeasure === UnitOfMeasure.lbs) {
          if (local?.quantity) {
            this.inventory.move(
              new MoveItem({
                product,
                sku,
                skus,
                batch,
                batches,
                productCase,
              }),
              this.mode,
              local.quantity
            )
          } else {
            this.toMeasure = {
              product,
              sku,
              skus,
              batch,
              batches,
              productCase,
            }
          }
        } else {
          this.inventory.move(
            new MoveItem({
              product,
              sku,
              skus,
              batch,
              batches,
              productCase,
            }),
            this.mode
          )
        }
        this.notify({})
        return [Action.None, undefined]
      }
      case Damaged.type: {
        this.inventory.move(new MoveItem({ product: scanned, skus: [] }), this.mode, 1)
        return [Action.None, undefined]
      }
      case Container.type:
      case Pallet.type:
      case Place.type:
      case ASN.type:
      case FreightShipment.type:
      case ParcelShipment.type: {
        const inventory = this.inventory.items.map((item) => {
          return {
            item: item.productCase?.guid || item.product?.object.guid,
            num: item.count,
            batch: item.batch?.guid,
          }
        })
        return [
          Action.Move,
          { source: this.source.object.guid, items: inventory, destination: scanned.object.guid },
        ]
      }
    }
    return [Action.Invalid, undefined]
  }

  caseSelector = (item: MoveItem) => {
    return (from: Product | Case, to: Product | Case) => {
      const oppositeMode = this.mode === Mode.Scan ? Mode.Unscan : Mode.Scan
      this.inventory.move(item, oppositeMode)
      const productCase = to.type === Case.type ? (to as Case) : undefined
      const newItem = new MoveItem({
        product: item.product,
        sku: item.sku,
        skus: item.skus,
        productCase,
      })
      this.inventory.move(newItem, this.mode)
      this.lastCaseMap[item.product.object.guid] = productCase
      this.notify({})
    }
  }

  batchSelector = (item: MoveItem) => {
    return (from: Batch | undefined, to: Batch) => {
      const oppositeMode = this.mode === Mode.Scan ? Mode.Unscan : Mode.Scan
      this.inventory.move(item, oppositeMode)
      const batch = to.type === Batch.type ? (to as Batch) : undefined
      const newItem = new MoveItem({
        product: item.product,
        sku: item.sku,
        skus: item.skus,
        productCase: item.productCase,
        batch: batch,
        batches: item.batches,
      })
      this.inventory.move(newItem, this.mode)
      this.lastBatchMap[item.product.object.guid] = batch
      this.notify({})
    }
  }

  provideMeasure = (item: MoveItemArgs) => {
    return (measure: number) => {
      this.inventory.move(new MoveItem(item), this.mode, measure)
      this.toMeasure = undefined
      this.notify({})
    }
  }

  setMode = (mode: Mode) => {
    this.mode = mode
  }

  getContext = () => {
    return { activity: 'moving', source: this.source.object.guid }
  }
}
